import { supabase } from '../lib/supabase';
import * as XLSX from 'xlsx';
import { PDFGenerator } from './pdfGenerator';
import { accountDeletionService } from './accountDeletionService';

interface SupabaseQueryResult<T> {
  data: T | null;
  error: Error | null;
}

// Helper to download files
const createDownload = (filename: string, content: Blob | string) => {
  const element = document.createElement('a');
  if (typeof content === 'string') {
    element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(content));
  } else {
    element.href = URL.createObjectURL(content);
  }
  element.setAttribute('download', filename);
  element.style.display = 'none';
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
  if (typeof content !== 'string') {
    URL.revokeObjectURL(element.href);
  }
};

export const privacyService = {
  async fetchAccountData() {
    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser();

    if (authError) {
      throw authError;
    }

    if (!user) {
      throw new Error('User not authenticated');
    }

    const userId = user.id;

    const profilePromise = supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();

    const postsPromise = supabase
      .from('posts')
      .select(`
        *,
        media_files (*)
      `)
      .eq('author_id', userId)
      .order('created_at', { ascending: false });

    const commentsAuthoredPromise = supabase
      .from('comments')
      .select(`
        *,
        post:posts (
          id,
          title,
          media_files (*),
          author:profiles!posts_author_id_fkey (
            username,
            full_name
          )
        )
      `)
      .eq('author_id', userId)
      .order('created_at', { ascending: false });

    const likesMadePromise = supabase
      .from('likes')
      .select(`
        *,
        post:posts (
          id,
          title,
          author:profiles!posts_author_id_fkey (
            username,
            full_name
          )
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    const collectionsPromise = supabase
      .from('collections')
      .select(`
        *,
        collection_posts (
          post_id,
          posts (
            id,
            title,
            created_at,
            media_files (*),
            author:profiles!posts_author_id_fkey (
              username,
              full_name
            )
          )
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    const followsFollowingPromise = supabase
      .from('follows')
      .select('*')
      .eq('follower_id', userId)
      .order('created_at', { ascending: false });

    const followsFollowersPromise = supabase
      .from('follows')
      .select('*')
      .eq('following_id', userId)
      .order('created_at', { ascending: false });

    const storiesPromise = supabase
      .from('stories')
      .select(`
        *,
        story_views (
          *,
          viewer:profiles!story_views_viewer_id_fkey (
            id,
            username,
            full_name
          )
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    const storyViewsPromise = supabase
      .from('story_views')
      .select(`
        *,
        story:stories (
          *,
          author:profiles (
            username,
            full_name
          )
        )
      `)
      .eq('viewer_id', userId)
      .order('viewed_at', { ascending: false });

    const conversationsPromise = supabase
      .from('conversations')
      .select(`
        *,
        messages (*),
        participant_one:profiles!conversations_participant_one_id_fkey (
          id,
          username,
          full_name,
          avatar_url
        ),
        participant_two:profiles!conversations_participant_two_id_fkey (
          id,
          username,
          full_name,
          avatar_url
        )
      `)
      .or(`participant_one_id.eq.${userId},participant_two_id.eq.${userId}`)
      .order('last_message_at', { ascending: false });

    const notificationsPromise = supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    const cartItemsPromise = supabase
      .from('cart_items')
      .select(`
        *,
        post:posts (
          id,
          title,
          price,
          media_files (*)
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    const wishlistItemsPromise = supabase
      .from('wishlist_items')
      .select(`
        *,
        post:posts (
          id,
          title,
          price,
          media_files (*)
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    const ordersAsBuyerPromise = supabase
      .from('orders')
      .select(`
        *,
        posts (
          id,
          title,
          price,
          description,
          media_files (*)
        ),
        seller:profiles!orders_seller_id_fkey (
          id,
          username,
          full_name
        )
      `)
      .eq('buyer_id', userId)
      .order('created_at', { ascending: false });

    const ordersAsSellerPromise = supabase
      .from('orders')
      .select(`
        *,
        posts (
          id,
          title,
          price,
          description,
          media_files (*)
        ),
        buyer:profiles!orders_buyer_id_fkey (
          id,
          username,
          full_name
        )
      `)
      .eq('seller_id', userId)
      .order('created_at', { ascending: false });

    const salesAsSellerPromise = supabase
      .from('sales')
      .select('*')
      .eq('seller_id', userId)
      .order('created_at', { ascending: false });

    const salesAsBuyerPromise = supabase
      .from('sales')
      .select('*')
      .eq('buyer_id', userId)
      .order('created_at', { ascending: false });

    const transactionsPromise = supabase
      .from('transactions')
      .select('*')
      .or(`buyer_id.eq.${userId},seller_id.eq.${userId}`)
      .order('created_at', { ascending: false });

    const paymentMethodsPromise = supabase
      .from('payment_methods')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    const paymentSettingsPromise = supabase
      .from('payment_settings')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    // Removed payoutsPromise as the table definition is uncertain and data is covered by transactions

    const reportsFiledPromise = supabase
      .from('reports')
      .select('*')
      .eq('reporter_id', userId)
      .order('created_at', { ascending: false });

    const reportsReceivedPromise = supabase
      .from('reports')
      .select('*')
      .eq('reported_item_type', 'profile')
      .eq('reported_item_id', userId)
      .order('created_at', { ascending: false });

    const collaborationsPromise = supabase
      .from('collaborations')
      .select('*')
      .eq('author_id', userId)
      .order('created_at', { ascending: false });

    const collaborationApplicationsPromise = supabase
      .from('collaboration_applications')
      .select('*')
      .eq('applicant_id', userId)
      .order('created_at', { ascending: false });

    const eventsHostedPromise = supabase
      .from('events')
      .select('*')
      .eq('organizer_id', userId)
      .order('start_date', { ascending: false });

    const eventAttendeesPromise = supabase
      .from('event_attendees')
      .select('*')
      .eq('user_id', userId)
      .order('registered_at', { ascending: false });

    const messagesSentPromise = supabase
      .from('messages')
      .select('*')
      .eq('sender_id', userId)
      .order('created_at', { ascending: false });

    const analyticsPromise = supabase
      .from('user_analytics')
      .select('*')
      .eq('user_id', userId)
      .order('date', { ascending: false });

    const [
      profileResult,
      postsResult,
      commentsAuthoredResult,
      likesMadeResult,
      collectionsResult,
      followsFollowingResult,
      followsFollowersResult,
      storiesResult,
      storyViewsResult,
      conversationsResult,
      notificationsResult,
      cartItemsResult,
      wishlistItemsResult,
      ordersAsBuyerResult,
      ordersAsSellerResult,
      salesAsSellerResult,
      salesAsBuyerResult,
      transactionsResult,
      paymentMethodsResult,
      paymentSettingsResult,
      reportsFiledResult,
      reportsReceivedResult,
      collaborationsResult,
      collaborationApplicationsResult,
      eventsHostedResult,
      eventAttendeesResult,
      messagesSentResult,
      analyticsResult,
    ] = await Promise.all([
      profilePromise,
      postsPromise,
      commentsAuthoredPromise,
      likesMadePromise,
      collectionsPromise,
      followsFollowingPromise,
      followsFollowersPromise,
      storiesPromise,
      storyViewsPromise,
      conversationsPromise,
      notificationsPromise,
      cartItemsPromise,
      wishlistItemsPromise,
      ordersAsBuyerPromise,
      ordersAsSellerPromise,
      salesAsSellerPromise,
      salesAsBuyerPromise,
      transactionsPromise,
      paymentMethodsPromise,
      paymentSettingsPromise,
      reportsFiledPromise,
      reportsReceivedPromise,
      collaborationsPromise,
      collaborationApplicationsPromise,
      eventsHostedPromise,
      eventAttendeesPromise,
      messagesSentPromise,
      analyticsPromise,
    ]);

    const handleResult = <T>(result: SupabaseQueryResult<T>, fallback: T): T => {
      if (result.error) {
        console.error('[privacyService] fetch error:', result.error);
        return fallback;
      }
      return result.data ?? fallback;
    };

    type RecordArray = Record<string, unknown>[];

    const profile = handleResult<Record<string, unknown> | null>(profileResult as SupabaseQueryResult<Record<string, unknown> | null>, null);
    const posts = handleResult<RecordArray>(postsResult as SupabaseQueryResult<RecordArray>, []);
    const commentsAuthored = handleResult<RecordArray>(commentsAuthoredResult as SupabaseQueryResult<RecordArray>, []);
    const likesMade = handleResult<RecordArray>(likesMadeResult as SupabaseQueryResult<RecordArray>, []);
    const collections = handleResult<RecordArray>(collectionsResult as SupabaseQueryResult<RecordArray>, []);
    const followsFollowing = handleResult<RecordArray>(followsFollowingResult as SupabaseQueryResult<RecordArray>, []);
    const followsFollowers = handleResult<RecordArray>(followsFollowersResult as SupabaseQueryResult<RecordArray>, []);
    const stories = handleResult<RecordArray>(storiesResult as SupabaseQueryResult<RecordArray>, []);
    const storyViews = handleResult<RecordArray>(storyViewsResult as SupabaseQueryResult<RecordArray>, []);
    const conversations = handleResult<RecordArray>(conversationsResult as SupabaseQueryResult<RecordArray>, []);
    const notifications = handleResult<RecordArray>(notificationsResult as SupabaseQueryResult<RecordArray>, []);
    const cartItems = handleResult<RecordArray>(cartItemsResult as SupabaseQueryResult<RecordArray>, []);
    const wishlistItems = handleResult<RecordArray>(wishlistItemsResult as SupabaseQueryResult<RecordArray>, []);
    const ordersAsBuyer = handleResult<RecordArray>(ordersAsBuyerResult as SupabaseQueryResult<RecordArray>, []);
    const ordersAsSeller = handleResult<RecordArray>(ordersAsSellerResult as SupabaseQueryResult<RecordArray>, []);
    const salesAsSeller = handleResult<RecordArray>(salesAsSellerResult as SupabaseQueryResult<RecordArray>, []);
    const salesAsBuyer = handleResult<RecordArray>(salesAsBuyerResult as SupabaseQueryResult<RecordArray>, []);
    const transactions = handleResult<RecordArray>(transactionsResult as SupabaseQueryResult<RecordArray>, []);
    const paymentMethods = handleResult<RecordArray>(paymentMethodsResult as SupabaseQueryResult<RecordArray>, []);
    const paymentSettings = handleResult<Record<string, unknown> | null>(paymentSettingsResult as SupabaseQueryResult<Record<string, unknown> | null>, null);
    const payouts: RecordArray = [];
    const reportsFiled = handleResult<RecordArray>(reportsFiledResult as SupabaseQueryResult<RecordArray>, []);
    const reportsReceived = handleResult<RecordArray>(reportsReceivedResult as SupabaseQueryResult<RecordArray>, []);
    const collaborations = handleResult<RecordArray>(collaborationsResult as SupabaseQueryResult<RecordArray>, []);
    const collaborationApplications = handleResult<RecordArray>(collaborationApplicationsResult as SupabaseQueryResult<RecordArray>, []);
    const eventsHosted = handleResult<RecordArray>(eventsHostedResult as SupabaseQueryResult<RecordArray>, []);
    const eventAttendees = handleResult<RecordArray>(eventAttendeesResult as SupabaseQueryResult<RecordArray>, []);
    const messagesSent = handleResult<RecordArray>(messagesSentResult as SupabaseQueryResult<RecordArray>, []);
    const analytics = handleResult<RecordArray>(analyticsResult as SupabaseQueryResult<RecordArray>, []);

    let likesReceived: RecordArray = [];
    let commentsOnPosts: RecordArray = [];
    const postIds = posts.map((post) => (post as { id?: string }).id).filter(Boolean) as string[];

    if (postIds.length) {
      const likesReceivedResult = await supabase
        .from('likes')
        .select(`
          *,
          user:profiles!likes_user_id_fkey (
            id,
            username,
            full_name
          )
        `)
        .in('post_id', postIds);
      likesReceived = handleResult<RecordArray>(
        { data: (likesReceivedResult.data ?? []) as RecordArray, error: likesReceivedResult.error as Error | null },
        [],
      );

      const commentsOnPostsResult = await supabase
        .from('comments')
        .select(`
          *,
          author:profiles!comments_author_id_fkey (
            id,
            username,
            full_name
          )
        `)
        .in('post_id', postIds);
      commentsOnPosts = handleResult<RecordArray>(
        { data: (commentsOnPostsResult.data ?? []) as RecordArray, error: commentsOnPostsResult.error as Error | null },
        [],
      );
    }

    const readLocalSettings = (key: string) => {
      try {
        if (typeof window === 'undefined') return {};
        const raw = window.localStorage.getItem(key);
        return raw ? JSON.parse(raw) : {};
      } catch {
        return {};
      }
    };

    return {
      meta: {
        generatedAt: new Date().toISOString(),
        userId,
        email: user.email,
      },
      profile,
      settings: {
        privacy: readLocalSettings('privacy-settings'),
        notifications: readLocalSettings('notification-settings'),
      },
      content: {
        posts,
        collections,
        stories,
      },
      interactions: {
        commentsAuthored,
        likesMade,
        likesReceived,
        commentsOnPosts,
        followsFollowing,
        followsFollowers,
        storyViews,
        notifications,
        messagesSent,
        conversations,
      },
      commerce: {
        cartItems,
        wishlistItems,
        orders: {
          asBuyer: ordersAsBuyer,
          asSeller: ordersAsSeller,
        },
        sales: {
          asSeller: salesAsSeller,
          asBuyer: salesAsBuyer,
        },
        transactions,
        paymentMethods,
        paymentSettings,
        payouts,
      },
      safety: {
        reportsFiled,
        reportsReceived,
        collaborations,
        collaborationApplications,
        eventsHosted,
        eventAttendees,
      },
      analytics,
    };
  },

  async downloadAccountData(format: 'json' | 'excel' | 'pdf' = 'json') {
    const data = await this.fetchAccountData();
    const userId = data.meta.userId;
    const dateStr = data.meta.generatedAt.split('T')[0];

    if (format === 'excel') {
      const wb = XLSX.utils.book_new();
      
      // Profile Sheet
      const profileData = [{ 
        ...data.profile,
        ...data.settings.privacy, // Flatten privacy settings into profile
        ...data.settings.notifications // Flatten notification settings
      }];
      const wsProfile = XLSX.utils.json_to_sheet(profileData);
      XLSX.utils.book_append_sheet(wb, wsProfile, "Profile & Settings");

      type R = Record<string, unknown>;

      if (data.content.posts.length) {
        const postsData = data.content.posts.map((p: R) => ({
          ...p,
          media_files_count: (p.media_files as R[] | undefined)?.length || 0,
          media_urls: (p.media_files as R[] | undefined)?.map((m: R) => m.url as string).join(', '),
          media_types: (p.media_files as R[] | undefined)?.map((m: R) => m.type as string).join(', ')
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(postsData), "Posts");
      }
      if (data.content.collections.length) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.content.collections as R[]), "Collections");
      }
      if (data.content.stories.length) {
        const storiesData = data.content.stories.map((s: R) => ({
          ...s,
          content_url: (s.content_url as string | undefined) || 'N/A',
          content_type: s.content_type,
          views_count: (s.story_views as R[] | undefined)?.length || 0,
          views_detail: JSON.stringify(s.story_views || [])
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(storiesData), "Stories");
      }

      if (data.interactions.commentsAuthored.length) {
        const commentsData = data.interactions.commentsAuthored.map((c: R) => ({
          ...c,
          post_title: (c.post as R | undefined)?.title || 'Unknown Post',
          post_author: ((c.post as R | undefined)?.author as R | undefined)?.username || 'Unknown'
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(commentsData), "Comments Authored");
      }
      if (data.interactions.commentsOnPosts.length) {
        const commentsReceivedData = data.interactions.commentsOnPosts.map((c: R) => ({
          ...c,
          author_username: (c.author as R | undefined)?.username || 'Unknown',
          author_email: (c.author as R | undefined)?.email || 'N/A'
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(commentsReceivedData), "Comments Received");
      }
      if (data.interactions.likesMade.length) {
        const likesMadeData = data.interactions.likesMade.map((l: R) => ({
          ...l,
          post_title: (l.post as R | undefined)?.title || 'Unknown Post',
          post_author: ((l.post as R | undefined)?.author as R | undefined)?.username || 'Unknown'
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(likesMadeData), "Likes Made");
      }
      if (data.interactions.likesReceived.length) {
        const likesReceivedData = data.interactions.likesReceived.map((l: R) => ({
          ...l,
          user_username: (l.user as R | undefined)?.username || 'Unknown'
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(likesReceivedData), "Likes Received");
      }
      if (data.interactions.followsFollowing.length) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.interactions.followsFollowing as R[]), "Following");
      }
      if (data.interactions.followsFollowers.length) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.interactions.followsFollowers as R[]), "Followers");
      }
      if (data.interactions.notifications.length) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.interactions.notifications as R[]), "Notifications");
      }
      
      if (data.interactions.conversations.length) {
        const messagesList: R[] = [];
        data.interactions.conversations.forEach((c: R) => {
          const messages = c.messages as R[] | undefined;
          if (messages && messages.length) {
            messages.forEach((m: R) => {
              messagesList.push({
                conversation_id: c.id,
                participant_one: (c.participant_one as R | undefined)?.username,
                participant_two: (c.participant_two as R | undefined)?.username,
                sender_id: m.sender_id,
                content: m.content,
                created_at: m.created_at,
                is_read: m.is_read
              });
            });
          }
        });
        if (messagesList.length) {
          XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(messagesList), "Messages");
        }
      }

      if (data.safety.eventsHosted.length) {
        const eventsData = data.safety.eventsHosted.map((e: R) => ({
          ...e,
          image_url: (e.image_url as string | undefined) || 'N/A'
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(eventsData), "Events Hosted");
      }
      if (data.safety.collaborations.length) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.safety.collaborations as R[]), "Collaborations");
      }
      
      if (data.commerce.orders.asBuyer.length) {
        const buyerOrdersData = data.commerce.orders.asBuyer.map((o: R) => ({
          ...o,
          post_title: (o.posts as R | undefined)?.title,
          post_price: (o.posts as R | undefined)?.price,
          seller_username: (o.seller as R | undefined)?.username,
          seller_email: (o.seller as R | undefined)?.email
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(buyerOrdersData), "Orders (Buyer)");
      }
      if (data.commerce.orders.asSeller.length) {
        const sellerOrdersData = data.commerce.orders.asSeller.map((o: R) => ({
          ...o,
          post_title: (o.posts as R | undefined)?.title,
          post_price: (o.posts as R | undefined)?.price,
          buyer_username: (o.buyer as R | undefined)?.username,
          buyer_email: (o.buyer as R | undefined)?.email
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sellerOrdersData), "Orders (Seller)");
      }

      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      createDownload(`soartistic-account-${userId}-${dateStr}.xlsx`, blob);
      return `soartistic-account-${userId}-${dateStr}.xlsx`;

    } else if (format === 'pdf') {
      const pdf = new PDFGenerator();
      
      pdf.addTitle("SoArtistic - Account Data Export");
      
      await pdf.addCard({
        "User ID": userId,
        "Date": dateStr,
        "Email": data.profile.email || 'N/A',
        "Username": data.profile.username || 'N/A',
        "Full Name": data.profile.full_name || 'N/A',
        "Location": data.profile.location || 'N/A',
        "Bio": data.profile.bio || 'N/A'
      }, data.profile.avatar_url, "Profile Information");

      // Posts
      if (data.content.posts.length) {
        pdf.addSectionTitle("Posts");
        await pdf.addPostGrid(data.content.posts);
      }

      if (data.content.stories.length) {
        pdf.addSectionTitle("Stories");
        type R = Record<string, unknown>;
        const storiesForGrid = data.content.stories.map((s: R) => ({
            title: (s.content_type as string | undefined) || 'Story',
            created_at: s.created_at as string,
            media_files: s.content_url ? [{ url: s.content_url as string }] : []
        }));
        await pdf.addPostGrid(storiesForGrid);
      }

      // Comments Authored
      if (data.interactions.commentsAuthored.length) {
        pdf.addSectionTitle("Comments Authored");
        for (const c of data.interactions.commentsAuthored) {
            await pdf.addCard({
                "Post": c.post?.title || 'Unknown',
                "Content": c.content,
                "Date": new Date(c.created_at).toLocaleDateString()
            });
        }
      }

      // Comments Received
      if (data.interactions.commentsOnPosts.length) {
          pdf.addSectionTitle("Comments Received");
          for (const c of data.interactions.commentsOnPosts) {
              await pdf.addCard({
                  "Author": c.author?.username || 'Unknown',
                  "Content": c.content,
                  "Date": new Date(c.created_at).toLocaleDateString()
              });
          }
      }

      // Messages
      if (data.interactions.conversations.length) {
          pdf.addSectionTitle("Messages");
          for (const c of data.interactions.conversations) {
              if (c.messages && c.messages.length) {
                  for (const m of c.messages) {
                      await pdf.addCard({
                          "From": m.sender_id === userId ? "Me" : "Other",
                          "Content": m.content,
                          "Date": new Date(m.created_at).toLocaleString()
                      });
                  }
              }
          }
      }

      // Events
      if (data.safety.eventsHosted.length) {
          pdf.addSectionTitle("Events Hosted");
          for (const e of data.safety.eventsHosted) {
              await pdf.addCard({
                  "Title": e.title,
                  "Date": new Date(e.start_date).toLocaleDateString(),
                  "Description": e.description || ''
              }, e.image_url, e.title);
          }
      }

      // Collaborations
      if (data.safety.collaborations.length) {
          pdf.addSectionTitle("Collaborations");
          for (const c of data.safety.collaborations) {
              await pdf.addCard({
                  "Title": c.title,
                  "Category": c.category,
                  "Status": c.status,
                  "Date": new Date(c.created_at).toLocaleDateString()
              });
          }
      }

      // Orders (Buyer)
      if (data.commerce.orders.asBuyer.length) {
          pdf.addSectionTitle("Orders (Buyer)");
          for (const o of data.commerce.orders.asBuyer) {
              await pdf.addCard({
                  "Order ID": o.id,
                  "Item": o.posts?.title || 'Unknown',
                  "Amount": o.total_amount?.toString() || '0',
                  "Status": o.status,
                  "Date": new Date(o.created_at).toLocaleDateString()
              });
          }
      }

      const filename = `soartistic-account-${userId}-${dateStr}.pdf`;
      pdf.save(filename);
      return filename;

    } else {
      // Default JSON
      const filename = `soartistic-account-${userId}-${dateStr}.json`;
      createDownload(filename, JSON.stringify(data, null, 2));
      return filename;
    }
  },

  async deleteAccount() {
    return accountDeletionService.requestAccountDeletion();
  },
};
